/**
 * Logging a meal you actually ate, built from components the way every meal in this
 * plan is built: from an ingredient list, with the totals computed rather than asserted
 * (section 3).
 *
 * Each component is a food looked up from Open Food Facts scaled to the grams you ate,
 * a food you have used before (which works with no signal), or macros typed in by hand.
 * The lookup is a convenience: with the database unreachable, everything still works.
 */
import { useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Banner } from './components.tsx';
import type { SlotName } from '../plan/types.ts';
import { customKey, customMealFor, plannedMealFor } from '../plan/nutrition.ts';
import {
  componentFromFood, componentFromMacros, totalOf, plausibility, mealPlausibility,
  cachedFoods, foodCacheKey, usable,
} from '../plan/foodLookup.ts';
import type { MealComponent, FoodResult } from '../plan/foodLookup.ts';
import { searchFoods, lookupBarcode } from '../data/foodSearch.ts';
import type { SearchOutcome } from '../data/foodSearch.ts';

type Mode = null | 'search' | 'barcode' | 'manual';

export function LogMeal({ api, dayIndex, slot, onClose }: {
  api: StoreApi; dayIndex: number; slot: SlotName; onClose: () => void;
}) {
  const existing = customMealFor(api.state, dayIndex, slot);
  const planned = plannedMealFor(api.state, dayIndex, slot);
  const [name, setName] = useState(existing?.n ?? '');
  const [items, setItems] = useState<MealComponent[]>(
    (existing as { items?: MealComponent[] })?.items ?? []);
  const [mode, setMode] = useState<Mode>(null);
  const [err, setErr] = useState<string | null>(null);

  const key = customKey(dayIndex, slot);
  const pending = api.pending.has(key);
  const total = totalOf(items);
  const delta = Math.round(total.k - planned.k);
  const mealWarnings = mealPlausibility(total);

  const addItem = (c: MealComponent, food?: FoodResult) => {
    setItems((prev) => [...prev, c]);
    setMode(null);
    // Remember the food so it is available with no signal next time.
    if (food?.code && usable(food.per100)) void api.put(foodCacheKey(food.code), food);
  };

  const save = async () => {
    setErr(null);
    if (name.trim() === '') { setErr('Give the meal a name so you recognise it later.'); return; }
    if (items.length === 0) { setErr('Add at least one thing you ate.'); return; }
    const ok = await api.put(key, {
      n: name.trim(), k: total.k, p: total.p, f: total.f, c: total.c, items,
    });
    if (!ok) { setErr('Not saved — the write did not return successfully.'); return; }
    await api.setFlag(`meal:${dayIndex}:${slot}`, true);
    onClose();
  };

  return (
    <div className="logmeal">
      <p className="sub" style={{ marginTop: 0 }}>
        Replacing <strong>{planned.n}</strong> ({planned.k} kcal, {planned.p} g protein).
      </p>

      <label className="label" htmlFor={`ln-${slot}`}>What was the meal?</label>
      <input id={`ln-${slot}`} value={name} onChange={(e) => setName(e.target.value)}
             placeholder="Curry and rice" />

      {items.length > 0 && (
        <div className="complist">
          {items.map((it, i) => (
            <div className="comp" key={i}>
              <span className="comp-main">
                <span className="comp-name">{it.n}</span>
                <span className="comp-sub">
                  {it.g !== null ? `${it.g}g · ` : ''}{it.k} kcal · {it.p} g P · {it.f} g F · {it.c} g C
                </span>
              </span>
              <button className="hidebtn" aria-label={`Remove ${it.n}`}
                      onClick={() => setItems((p) => p.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <div className="row comp-total">
            <span className="k">Meal total</span>
            <span className="v">
              <strong>{total.k} kcal</strong>
              <span className="dim"> · {total.p} g P · {total.f} g F · {total.c} g C
                {' · '}{delta >= 0 ? '+' : ''}{delta} vs planned</span>
            </span>
          </div>
        </div>
      )}

      {mealWarnings.map((w) => <Banner kind="warn" key={w.text}>{w.text}</Banner>)}

      {mode === null && (
        <div className="btns">
          <button className="small" onClick={() => setMode('search')}>Search food</button>
          <button className="small" onClick={() => setMode('barcode')}>Barcode</button>
          <button className="small" onClick={() => setMode('manual')}>Type macros</button>
        </div>
      )}

      {mode === 'search' && <Finder api={api} kind="search" onAdd={addItem} onCancel={() => setMode(null)} />}
      {mode === 'barcode' && <Finder api={api} kind="barcode" onAdd={addItem} onCancel={() => setMode(null)} />}
      {mode === 'manual' && <Manual onAdd={(c) => addItem(c)} onCancel={() => setMode(null)} />}

      <p className="sub">
        Calories are worked out from the macros at 4 kcal a gram for protein and carbs and
        9 for fat. The plan never uses a calorie figure that was not calculated, and neither
        does this.
      </p>

      <div className="btns">
        <button className="primary" disabled={!api.durable || pending || items.length === 0}
                onClick={() => void save()}>
          {pending ? 'Saving…' : existing ? 'Update' : 'Log this meal'}
        </button>
        {existing && (
          <button className="danger" disabled={!api.durable || pending}
                  onClick={async () => { if (await api.del(key)) onClose(); }}>
            Back to the planned meal
          </button>
        )}
        <button onClick={onClose}>Cancel</button>
      </div>
      {err && <Banner kind="bad">{err}</Banner>}
      {api.errorFor(key) && <Banner kind="bad" title="Not saved">{api.errorFor(key)}</Banner>}
    </div>
  );
}

/** Search or barcode, then a portion. Falls back to foods you have used before. */
function Finder({ api, kind, onAdd, onCancel }: {
  api: StoreApi; kind: 'search' | 'barcode';
  onAdd: (c: MealComponent, food?: FoodResult) => void; onCancel: () => void;
}) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SearchOutcome | null>(null);
  const [picked, setPicked] = useState<FoodResult | null>(null);
  const [grams, setGrams] = useState('');
  const cached = cachedFoods(api.state);

  const run = async () => {
    setBusy(true); setPicked(null);
    try { setOutcome(await (kind === 'barcode' ? lookupBarcode(q) : searchFoods(q))); }
    finally { setBusy(false); }
  };

  if (picked) {
    const warn = plausibility(picked.per100);
    const g = Number(grams);
    const comp = Number.isFinite(g) && g > 0 ? componentFromFood(picked, g) : null;
    return (
      <div className="finder">
        <div className="ex-name">{picked.name}{picked.brand && `, ${picked.brand}`}</div>
        <div className="sub">
          Per 100g: {picked.per100!.k} kcal · {picked.per100!.p} g P · {picked.per100!.f} g F
          {' · '}{picked.per100!.c} g C{picked.servingSize && ` · serving ${picked.servingSize}`}
        </div>
        {warn.map((w) => (
          <Banner kind={w.severity === 'block' ? 'bad' : 'warn'} key={w.text}>{w.text}</Banner>
        ))}
        <label className="label">How many grams did you eat?</label>
        <input type="text" inputMode="decimal" value={grams} autoFocus
               onChange={(e) => setGrams(e.target.value)} placeholder="200" />
        {comp && (
          <div className="row" style={{ marginTop: 6 }}>
            <span className="k">That portion</span>
            <span className="v">{comp.k} kcal · {comp.p} g P · {comp.f} g F · {comp.c} g C</span>
          </div>
        )}
        <div className="btns">
          <button className="small primary" disabled={!comp || !usable(picked.per100)}
                  onClick={() => onAdd(comp!, picked)}>Add to the meal</button>
          <button className="small" onClick={() => setPicked(null)}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="finder">
      <label className="label">
        {kind === 'barcode' ? 'Barcode from the packet' : 'Search Open Food Facts'}
      </label>
      <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
             inputMode={kind === 'barcode' ? 'numeric' : 'text'}
             placeholder={kind === 'barcode' ? '5000157024671' : 'chicken breast'}
             onKeyDown={(e) => { if (e.key === 'Enter') void run(); }} />
      <div className="btns">
        <button className="small primary" disabled={busy} onClick={() => void run()}>
          {busy ? 'Looking…' : kind === 'barcode' ? 'Look up' : 'Search'}
        </button>
        <button className="small" onClick={onCancel}>Cancel</button>
      </div>

      {outcome?.error && <Banner kind="warn">{outcome.error}</Banner>}
      {outcome?.note && <Banner kind="info">{outcome.note}</Banner>}
      {outcome?.ok && outcome.results.length === 0 && !outcome.note && (
        <Banner kind="warn">Nothing found. Try different words, or type the macros in.</Banner>
      )}

      {outcome?.results.map((r) => (
        <button className="pickrow" key={r.code + r.name} onClick={() => setPicked(r)}>
          <span className="pickrow-sess">
            {r.name}{r.brand && <span className="dim"> · {r.brand}</span>}
            <span className="comp-sub">
              per 100g: {r.per100!.k} kcal · {r.per100!.p} g P · {r.per100!.f} g F · {r.per100!.c} g C
            </span>
          </span>
          <span className="pickrow-go">›</span>
        </button>
      ))}

      {cached.length > 0 && (
        <>
          <div className="label" style={{ marginTop: 12 }}>
            Foods you have used before ({cached.length}) — these work with no signal
          </div>
          {cached.slice(0, 8).map((r) => (
            <button className="pickrow" key={`c-${r.code}`} onClick={() => setPicked(r)}>
              <span className="pickrow-sess">
                {r.name}{r.brand && <span className="dim"> · {r.brand}</span>}
              </span>
              <span className="pickrow-go">›</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

function Manual({ onAdd, onCancel }: {
  onAdd: (c: MealComponent) => void; onCancel: () => void;
}) {
  const [n, setN] = useState(''); const [p, setP] = useState('');
  const [f, setF] = useState(''); const [c, setC] = useState('');
  const comp = componentFromMacros(n, Number(p), Number(f), Number(c));
  return (
    <div className="finder">
      <label className="label">What was it?</label>
      <input value={n} onChange={(e) => setN(e.target.value)} autoFocus placeholder="Pint of lager" />
      <div className="entryrow" style={{ marginTop: 8 }}>
        <label className="ef"><span className="label">protein g</span>
          <input type="text" inputMode="decimal" value={p} onChange={(e) => setP(e.target.value)} /></label>
        <label className="ef"><span className="label">fat g</span>
          <input type="text" inputMode="decimal" value={f} onChange={(e) => setF(e.target.value)} /></label>
        <label className="ef"><span className="label">carbs g</span>
          <input type="text" inputMode="decimal" value={c} onChange={(e) => setC(e.target.value)} /></label>
      </div>
      {comp && (
        <div className="row" style={{ marginTop: 6 }}>
          <span className="k">Works out at</span><span className="v">{comp.k} kcal</span>
        </div>
      )}
      <div className="btns">
        <button className="small primary" disabled={!comp} onClick={() => onAdd(comp!)}>
          Add to the meal
        </button>
        <button className="small" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
